/* PatientInsightsDrawer — Wave 3 compliance deep-dive.
 *
 * Centered modal dialog (iter98.8 — was a right-side slide-in drawer
 * pre-iter98.8; the right-edge anchor on a 14"/15" laptop crammed the
 * 60-day chart, the 4-stat strip and the Planned vs Actual row into a
 * thin column. Moving to a wide centered popup gives every section
 * room to breathe and matches the "open in a modal" pattern the rest
 * of the doctor portal uses for deep-dives.) Surfaces four things the
 * doctor needs in one place without leaving the dashboard:
 *
 *   1. Wear chart        — 60-day daily wear hours + aggregate stats
 *   2. Stage history     — every aligner-stage change with timestamp + author
 *   3. Issue timeline    — every issue raised, severity, status, resolution
 *   4. Nudge history     — every WhatsApp / SMS / phone nudge fired, outcome
 *
 * Powered by `GET /api/doctor/insights/patient/{patient_id}`.
 *
 * Why a modal, not a page?
 * - Doctors want quick scan-and-act, not navigate-and-bounce.
 * - The table is already filtered to the relevant patients; staying in
 *   context lets them sweep through 5–10 at-risk patients in 2 min.
 * - The "Open full profile" button is still right there for deeper work.
 *
 * The component name `PatientInsightsDrawer` is preserved to avoid a
 * cascading rename across AlignerInsights.jsx and any other call sites.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { waLink, waReason } from "@/lib/wa";
import { statusLabel as sharedStatusLabel } from "@/lib/caseStatus";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
    UserCircle, ArrowSquareOut, CalendarBlank, ChatCircleDots,
    Warning, CheckCircle, ClockCountdown, ShieldWarning,
    TrendUp, TrendDown, Stack, Bell, ArrowsClockwise, Spinner,
    X,
} from "@phosphor-icons/react";

const BUCKET_TONE = {
    "needs-attention": "bg-amber-100 text-amber-800 border-amber-200",
    "at-risk":         "bg-amber-50 text-amber-700 border-amber-200",
    "on-track":        "bg-primary/10 text-primary border-primary/20",
    "pending":         "bg-foreground/5 text-foreground/70 border-foreground/15",
};

const SEVERITY_TONE = {
    high:   "bg-rose-100 text-rose-700 border-rose-200",
    medium: "bg-amber-100 text-amber-800 border-amber-200",
    low:    "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const NUDGE_OUTCOME = {
    pending:         { label: "Pending",     tone: "bg-amber-100 text-amber-800",      Icon: ClockCountdown },
    resolved_auto:   { label: "Auto-resolved", tone: "bg-emerald-100 text-emerald-700", Icon: CheckCircle },
    resolved_manual: { label: "Resolved",    tone: "bg-emerald-100 text-emerald-700",   Icon: CheckCircle },
    dismissed:       { label: "Dismissed",   tone: "bg-zinc-200 text-zinc-700",         Icon: X },
};

/* iter101.0 — Case status labels now source from `/lib/caseStatus.js`
   so this drawer speaks the same language as the rest of the doctor
   portal. Kept the local const removed. */


function relTime(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return "—";
    const days = Math.floor(ms / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
}


export default function PatientInsightsDrawer({ open, onClose, patientId, caseId, bucket }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !patientId) return;
        setLoading(true);
        setError(null);
        const params = caseId ? `?case_id=${encodeURIComponent(caseId)}` : "";
        api.get(`/doctor/insights/patient/${patientId}${params}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Couldn't load deep-dive"))
            .finally(() => setLoading(false));
    }, [open, patientId, caseId]);

    // Reset state when the drawer fully closes so re-opening on a
    // different patient doesn't briefly flash the previous one.
    useEffect(() => {
        if (!open) {
            setData(null);
            setError(null);
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent
                className="max-w-3xl lg:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 gap-0 [&>button]:hidden"
                data-testid="patient-insights-drawer"
            >
                {/* Always-mounted a11y title + description. Radix Dialog
                    requires both even when our richer header replaces them
                    in the visible UI. The visible header lives inside
                    DrawerBody and uses its own DialogTitle for the patient
                    name; this VisuallyHidden one is the fallback for the
                    loading / error states so screen readers always have
                    something. */}
                <VisuallyHidden>
                    <DialogTitle>Patient compliance deep-dive</DialogTitle>
                    <DialogDescription>
                        Wear chart, stage history, issues and nudges for the selected patient.
                    </DialogDescription>
                </VisuallyHidden>

                {loading && (
                    <div className="p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
                        <Spinner size={20} className="animate-spin" />
                        Loading deep-dive…
                    </div>
                )}

                {!loading && error && (
                    <div className="p-10 text-center text-sm text-rose-600">{error}</div>
                )}

                {!loading && data && (
                    <DrawerBody data={data} bucket={bucket} onClose={onClose} />
                )}
            </DialogContent>
        </Dialog>
    );
}


function DrawerBody({ data, bucket, onClose }) {
    const { patient, case: c, wear_series, wear_stats, issues, issues_stats,
            stage_history, nudges, nudges_stats, last_touch, compliance } = data;
    const bucketTone = BUCKET_TONE[bucket] || BUCKET_TONE["pending"];

    return (
        <div className="flex flex-col">
            {/* HEADER */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-border bg-secondary/30 space-y-0">
                <div className="flex items-start gap-3 text-left">
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg shrink-0">
                        {(patient.name || "P").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                        <DialogTitle className="text-lg leading-tight truncate text-left" data-testid="drawer-patient-name">
                            {patient.name}
                        </DialogTitle>
                        <DialogDescription className="text-xs mt-0.5 text-left">
                            {c.code && <span className="font-mono">{c.code}</span>}
                            {c.diagnosis ? <> · {c.diagnosis}</> : null}
                            {c.status && <> · <span className="font-semibold">{sharedStatusLabel(c.status, "doctor") || c.status.replace(/_/g, " ")}</span></>}
                        </DialogDescription>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {bucket && (
                                <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${bucketTone}`}
                                    data-testid="drawer-bucket-pill"
                                >
                                    {bucket.replace("-", " ")}
                                </span>
                            )}
                            {c.current_stage > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-card border border-border text-muted-foreground">
                                    <Stack size={10} weight="bold" />
                                    Stage {c.current_stage}/{c.total_stages || "—"}
                                </span>
                            )}
                            {last_touch?.at && (
                                <span className="text-[10px] text-muted-foreground">
                                    Last touch: {relTime(last_touch.at)} ({last_touch.type === "patient_message" ? "chat" : last_touch.type || "—"})
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="shrink-0 w-7 h-7 rounded-full hover:bg-card grid place-items-center"
                        aria-label="Close"
                        data-testid="drawer-close"
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* Action row */}
                <div className="flex flex-wrap gap-2 mt-3">
                    <Button asChild size="sm" variant="outline" className="rounded-full h-8 text-xs">
                        <Link to={`/doctor/patients/${patient.id}`} data-testid="drawer-open-profile">
                            <UserCircle size={12} className="mr-1" weight="duotone" />
                            Full profile
                            <ArrowSquareOut size={11} className="ml-1" />
                        </Link>
                    </Button>
                    <Button asChild size="sm" variant="outline" className="rounded-full h-8 text-xs">
                        <Link to="/doctor/appointments" data-testid="drawer-book">
                            <CalendarBlank size={12} className="mr-1" weight="duotone" />
                            Book
                        </Link>
                    </Button>
                    {(() => {
                        const wa = waLink(
                            patient.phone,
                            `Hi ${(patient.name || "").split(" ")[0] || "there"}, quick check-in from your aligner team. How are things going?`,
                        );
                        if (wa) {
                            return (
                                <a
                                    href={wa}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                                    data-testid="drawer-whatsapp"
                                >
                                    <ChatCircleDots size={11} weight="duotone" />
                                    WhatsApp
                                </a>
                            );
                        }
                        return (
                            <span
                                className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium bg-zinc-100 text-muted-foreground border border-zinc-200 cursor-not-allowed"
                                title={waReason(patient.phone) || "No phone"}
                                data-testid="drawer-whatsapp-disabled"
                            >
                                <ChatCircleDots size={11} weight="duotone" />
                                WhatsApp
                            </span>
                        );
                    })()}
                </div>
            </DialogHeader>

            {/* BODY */}
            <div className="px-6 pb-8 pt-5 space-y-6">
                <WearChart series={wear_series} stats={wear_stats} />
                {/* iter98.3 — Phase C — Compliance summary surfaces the
                    planned-vs-actual story from Phase B self-advances.
                    Self-hides when there are no self-advance events
                    yet (data only populates once the patient uses the
                    "Switch now" button on their wear-schedule card). */}
                {compliance && compliance.self_advances_total > 0 && (
                    <Section
                        title="Schedule adherence"
                        icon={TrendUp}
                        testid="drawer-compliance"
                    >
                        <ComplianceSummary
                            data={compliance}
                            currentStage={c.current_stage}
                            totalStages={c.total_stages}
                        />
                    </Section>
                )}
                <Section
                    title="Stage history"
                    icon={Stack}
                    count={stage_history.length}
                    testid="drawer-stage-history"
                >
                    <StageHistory items={stage_history} currentStage={c.current_stage} totalStages={c.total_stages} />
                </Section>
                <Section
                    title="Issue timeline"
                    icon={ShieldWarning}
                    count={issues_stats.total}
                    badge={issues_stats.open > 0 ? { label: `${issues_stats.open} open`, tone: "bg-rose-100 text-rose-700" } : null}
                    testid="drawer-issues"
                >
                    <IssueTimeline items={issues} />
                </Section>
                <Section
                    title="Nudge history"
                    icon={Bell}
                    count={nudges_stats.total}
                    badge={nudges_stats.pending > 0 ? { label: `${nudges_stats.pending} pending`, tone: "bg-amber-100 text-amber-800" } : null}
                    testid="drawer-nudges"
                >
                    <NudgeHistory items={nudges} />
                </Section>
            </div>
        </div>
    );
}


/* ─────── Section wrapper ─────── */
function Section({ title, icon: Icon, count, badge, children, testid }) {
    return (
        <section data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon size={15} weight="duotone" className="text-primary" />}
                <h3 className="text-sm font-bold tracking-tight">{title}</h3>
                {typeof count === "number" && (
                    <span className="text-[10px] tabular-nums font-bold text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                        {count}
                    </span>
                )}
                {badge && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.tone}`}>
                        {badge.label}
                    </span>
                )}
            </div>
            {children}
        </section>
    );
}


/* ─────── Wear chart — 60d bar chart + stats strip ─────── */
function WearChart({ series, stats }) {
    const width = 560;
    const height = 110;
    const gap = 1;
    const n = series.length || 60;
    const barWidth = (width - gap * (n - 1)) / n;
    const maxH = Math.max(24, ...(series || []).map((s) => s.hours || 0));
    const targetY = height - (20 / maxH) * height;       // 20h/day target line
    const recoveryY = height - (16 / maxH) * height;     // 16h "at-risk" boundary
    const series60 = series || [];

    const todayIdx = series60.length - 1;
    const last7 = series60.slice(-7).filter((s) => s.logged);
    const prior7 = series60.slice(-14, -7).filter((s) => s.logged);
    const avg7 = last7.length ? (last7.reduce((a, s) => a + s.hours, 0) / last7.length) : null;
    const avgPrior = prior7.length ? (prior7.reduce((a, s) => a + s.hours, 0) / prior7.length) : null;
    const trend = (avg7 != null && avgPrior != null)
        ? (avg7 - avgPrior > 1 ? "up" : avg7 - avgPrior < -1 ? "down" : "flat")
        : "flat";

    return (
        <Section title="Wear time — last 60 days" icon={Stack} testid="drawer-wear-chart">
            {/* Stats strip */}
            <div className="grid grid-cols-4 gap-2 mb-3">
                <Stat label="60d avg" value={stats.avg_60d != null ? `${stats.avg_60d}h` : "—"} />
                <Stat label="Logged days" value={`${stats.logged_days}/${stats.window_days}`} />
                <Stat label="On-track days" value={String(stats.on_track_days)} sub="≥20h" />
                <Stat
                    label="7d trend"
                    value={avg7 != null ? `${avg7.toFixed(1)}h` : "—"}
                    icon={trend === "up" ? TrendUp : trend === "down" ? TrendDown : null}
                    iconCls={trend === "up" ? "text-emerald-600" : "text-rose-600"}
                />
            </div>

            {/* Bar chart */}
            <div className="bg-card border border-border rounded-xl p-3" data-testid="drawer-wear-svg-wrap">
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto block" style={{ height: 110 }}>
                    {/* Target line at 20h */}
                    <line x1="0" y1={targetY} x2={width} y2={targetY}
                          stroke="rgba(5,150,105,0.4)" strokeWidth="1" strokeDasharray="3 3" />
                    <text x={width - 2} y={targetY - 2} textAnchor="end" fontSize="9" fill="rgba(5,150,105,0.7)" fontWeight="bold">
                        20h target
                    </text>
                    {/* At-risk boundary at 16h */}
                    <line x1="0" y1={recoveryY} x2={width} y2={recoveryY}
                          stroke="rgba(217,119,6,0.3)" strokeWidth="1" strokeDasharray="2 2" />
                    {series60.map((s, i) => {
                        const h = s.hours > 0 ? Math.max(2, (s.hours / maxH) * height) : 2;
                        const x = i * (barWidth + gap);
                        const y = height - h;
                        let color = "#e5e7eb";   // grey for missed
                        if (s.logged) {
                            color = s.hours >= 20 ? "#059669"       // emerald
                                : s.hours >= 16 ? "#10b981"          // emerald-500
                                : s.hours >= 12 ? "#d97706"          // amber-600
                                : "#dc2626";                          // rose
                        }
                        const isToday = i === todayIdx;
                        return (
                            <rect
                                key={s.date}
                                x={x}
                                y={y}
                                width={barWidth}
                                height={h}
                                fill={color}
                                stroke={isToday ? "#111" : "none"}
                                strokeWidth={isToday ? 0.5 : 0}
                                rx="0.5"
                                data-testid={isToday ? "drawer-wear-bar-today" : undefined}
                            >
                                <title>{`${s.date}: ${s.logged ? `${s.hours.toFixed(1)}h` : "no log"}`}</title>
                            </rect>
                        );
                    })}
                </svg>
                <div className="flex items-center justify-between mt-2 text-[9px] text-muted-foreground font-mono">
                    <span>{series60[0]?.date}</span>
                    <div className="flex items-center gap-3">
                        <LegendDot color="#059669" label="≥20h" />
                        <LegendDot color="#d97706" label="12-16h" />
                        <LegendDot color="#dc2626" label="<12h" />
                        <LegendDot color="#e5e7eb" label="no log" />
                    </div>
                    <span>Today</span>
                </div>
            </div>
        </Section>
    );
}


function LegendDot({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
            {label}
        </span>
    );
}


function Stat({ label, value, sub, icon: Icon, iconCls }) {
    return (
        <div className="bg-card border border-border rounded-xl p-2.5">
            <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="flex items-baseline gap-1 mt-0.5">
                <span className="font-display text-lg font-bold tabular-nums leading-none">{value}</span>
                {Icon && <Icon size={12} weight="bold" className={iconCls} />}
            </div>
            {sub && <div className="text-[9px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}


/* ─────── Stage history ─────── */
function StageHistory({ items, currentStage, totalStages }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-xs text-muted-foreground bg-secondary/30 rounded-xl p-3" data-testid="drawer-stage-empty">
                {currentStage > 0
                    ? `Currently on stage ${currentStage}${totalStages ? `/${totalStages}` : ""}. No stage transitions recorded yet — history will populate from the next aligner update.`
                    : "No stage transitions yet. Will populate once the doctor advances the aligner stage."}
            </div>
        );
    }
    return (
        <ul className="space-y-1.5">
            {items.map((it) => {
                const isReversion = it.action === "aligner_stage_reverted";
                const Icon = isReversion ? ArrowsClockwise : Stack;
                // iter98.3 — Phase C deviation badge per row. Only
                // self-advance entries carry deviation data; clinic-side
                // edits (`self_advanced: false`) render the old chrome.
                const isSelf = !!it.self_advanced;
                const kind = it.deviation_kind;
                const badge =
                    kind === "early"
                        ? { label: `${Math.abs(it.deviation_days)}d early`, tone: "bg-amber-100 text-amber-800 border-amber-200" }
                        : kind === "late"
                            ? { label: `${it.deviation_days}d late`, tone: "bg-sky-100 text-sky-800 border-sky-200" }
                            : kind === "on_schedule"
                                ? { label: "On schedule", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" }
                                : null;
                return (
                    <li
                        key={it.id}
                        className="flex items-start gap-3 bg-card border border-border rounded-xl px-3 py-2"
                        data-testid={`drawer-stage-row-${it.id}`}
                    >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                            isReversion ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"
                        }`}>
                            <Icon size={12} weight="bold" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold">{it.note || "Stage updated"}</span>
                                {badge && (
                                    <span className={`text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.tone}`}>
                                        {badge.label}
                                    </span>
                                )}
                                {isSelf && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                                        Patient
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                {it.author_name || "—"} · {relTime(it.created_at)}
                                {it.expected_days && it.actual_days != null && (
                                    <span> · {it.actual_days}d worn vs {it.expected_days}d planned</span>
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}


/* ─────── Compliance summary (Phase C) ─────── */
function ComplianceSummary({ data, currentStage, totalStages }) {
    const {
        self_advances_total: total,
        early, late, on_schedule,
        avg_deviation_days: avgDev,
        net_offset_days: net,
        total_expected_days: expDays,
        total_actual_days: actDays,
        on_time_rate: onTimeRate,
    } = data;

    // Headline copy reflects the net offset — most useful single
    // signal: is the patient overall ahead of, behind, or with the plan?
    const headline =
        net <= -2
            ? { tone: "amber", label: `${Math.abs(net)} days ahead of plan`, sub: "Patient is consistently switching aligners early." }
            : net >= 2
                ? { tone: "sky", label: `${net} days behind plan`, sub: "Patient is taking longer between aligners than planned." }
                : { tone: "emerald", label: "On schedule overall", sub: "Patient is keeping pace with the plan." };

    const headlineCls = {
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    }[headline.tone];
    const headlineDot = {
        amber: "bg-amber-500",
        sky: "bg-sky-500",
        emerald: "bg-emerald-500",
    }[headline.tone];

    return (
        <div className="space-y-3" data-testid="drawer-compliance-summary">
            {/* Net-offset headline */}
            <div className={`rounded-xl border p-3.5 ${headlineCls}`}>
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${headlineDot}`} />
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-80">Net offset</div>
                </div>
                <div className="text-base font-bold mt-1 leading-tight">{headline.label}</div>
                <div className="text-[11px] opacity-80 mt-0.5 leading-snug">{headline.sub}</div>
            </div>

            {/* Stat grid */}
            <div className="grid grid-cols-4 gap-2">
                <StatTile label="On time" value={on_schedule} total={total} tone="emerald" testid="compliance-stat-on-time" />
                <StatTile label="Early" value={early} total={total} tone="amber" testid="compliance-stat-early" />
                <StatTile label="Late" value={late} total={total} tone="sky" testid="compliance-stat-late" />
                <StatTile
                    label="Adherence"
                    value={onTimeRate != null ? `${onTimeRate}%` : "—"}
                    tone="primary"
                    testid="compliance-stat-rate"
                />
            </div>

            {/* Planned vs actual mini-strip */}
            <div className="rounded-xl border border-border bg-secondary/30 p-3">
                <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Planned vs actual</div>
                    {avgDev != null && (
                        <div className="text-[10.5px] text-muted-foreground tabular-nums">
                            avg {avgDev > 0 ? `+${avgDev}` : avgDev}d / switch
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <div className="flex-1">
                        <div className="text-[10px] text-muted-foreground">Planned</div>
                        <div className="font-semibold tabular-nums">{expDays}d</div>
                    </div>
                    <div className="text-muted-foreground">→</div>
                    <div className="flex-1">
                        <div className="text-[10px] text-muted-foreground">Actual</div>
                        <div className="font-semibold tabular-nums">{actDays}d</div>
                    </div>
                    <div className="text-muted-foreground">·</div>
                    <div className="flex-1">
                        <div className="text-[10px] text-muted-foreground">Stage</div>
                        <div className="font-semibold tabular-nums">{currentStage}/{totalStages || "—"}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatTile({ label, value, total, tone = "primary", testid }) {
    const toneCls = {
        emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
        amber: "bg-amber-50 text-amber-900 border-amber-200",
        sky: "bg-sky-50 text-sky-900 border-sky-200",
        primary: "bg-primary/5 text-primary border-primary/20",
    }[tone];
    return (
        <div className={`rounded-lg border p-2 text-center ${toneCls}`} data-testid={testid}>
            <div className="text-base font-bold leading-none tabular-nums">{value}</div>
            <div className="text-[9.5px] uppercase tracking-wider font-semibold mt-1 opacity-80">
                {label}
                {total != null && typeof value === "number" && (
                    <span className="opacity-70"> / {total}</span>
                )}
            </div>
        </div>
    );
}


/* ─────── Issue timeline ─────── */
function IssueTimeline({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-xs text-muted-foreground bg-secondary/30 rounded-xl p-3" data-testid="drawer-issues-empty">
                No issues reported on this case. 🎉
            </div>
        );
    }
    return (
        <ul className="space-y-2">
            {items.map((it) => {
                const tone = SEVERITY_TONE[it.severity] || SEVERITY_TONE.low;
                const resolved = it.status === "resolved";
                return (
                    <li
                        key={it.id}
                        className={`bg-card border border-border rounded-xl p-3 ${resolved ? "opacity-75" : ""}`}
                        data-testid={`drawer-issue-${it.id}`}
                    >
                        <div className="flex items-start gap-2 flex-wrap">
                            <Warning
                                size={14}
                                weight={resolved ? "regular" : "fill"}
                                className={resolved ? "text-muted-foreground" : "text-rose-500"}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-semibold">
                                        {String(it.issue_type || it.type || "Issue").replace(/_/g, " ")}
                                    </span>
                                    {it.severity && (
                                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${tone}`}>
                                            {it.severity}
                                        </span>
                                    )}
                                    {resolved ? (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                                            Resolved
                                        </span>
                                    ) : (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                            Open
                                        </span>
                                    )}
                                </div>
                                {it.note && (
                                    <p className="text-xs mt-1 text-foreground/80">{it.note}</p>
                                )}
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    Raised {relTime(it.created_at)}
                                    {resolved && it.resolved_at && <> · resolved {relTime(it.resolved_at)}</>}
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}


/* ─────── Nudge history ─────── */
function NudgeHistory({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-xs text-muted-foreground bg-secondary/30 rounded-xl p-3" data-testid="drawer-nudges-empty">
                No nudges sent yet. Use the WhatsApp button above to send one — it'll be logged here.
            </div>
        );
    }
    return (
        <ul className="space-y-2">
            {items.map((n) => {
                const meta = NUDGE_OUTCOME[n.outcome] || NUDGE_OUTCOME.pending;
                return (
                    <li
                        key={n.id}
                        className="bg-card border border-border rounded-xl p-3"
                        data-testid={`drawer-nudge-${n.id}`}
                    >
                        <div className="flex items-start gap-2">
                            <div className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                <ChatCircleDots size={12} weight="duotone" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-semibold">
                                        {(n.channel || "whatsapp").toUpperCase()}
                                    </span>
                                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${meta.tone}`}>
                                        <meta.Icon size={9} weight="fill" className="inline mr-0.5" />
                                        {meta.label}
                                    </span>
                                    {n.reason && (
                                        <span className="text-[10px] text-muted-foreground">{n.reason.replace(/_/g, " ")}</span>
                                    )}
                                </div>
                                {n.message && (
                                    <p className="text-xs mt-1 text-foreground/80 italic line-clamp-2">"{n.message}"</p>
                                )}
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    {n.doctor_name || "—"} · {relTime(n.sent_at)}
                                    {n.resolved_reason && <> · {n.resolved_reason}</>}
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
